"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { confirmMatureContent, getMatureContentConfirmation } from "@/lib/actions/mature-content-actions";
const confirmationEvent = "iloris:mature-content-confirmed";
const serverSnapshot = () => false;
function subscribe(onChange: () => void) {
  window.addEventListener(confirmationEvent, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(confirmationEvent, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** The owner component must be keyed by account and story to isolate in-flight work. */
export function useMatureContentConfirmation(storyId: string, userId: string | null) {
  const [status, setStatus] = useState<"checking" | "warning" | "saving" | "accepted">(userId ? "checking" : "warning");
  const [error, setError] = useState<string | null>(null);
  const storageKey = `iloris:mature-content:${storyId}`;
  const guestAccepted = useSyncExternalStore(subscribe, () => {
    if (userId) return false;
    try {
      return sessionStorage.getItem(storageKey) === "confirmed";
    } catch {
      return false;
    }
  }, serverSnapshot);
  useEffect(() => {
    if (!userId) return;
    let active = true;
    void getMatureContentConfirmation(storyId).then(result => {
      if (!active) return;
      if (result.userId !== userId) throw new Error("Session changed");
      setStatus(result.accepted ? "accepted" : "warning");
    }).catch(() => {
      if (!active) return;
      setError("Could not check your saved confirmation. Please confirm again or reload the page.");
      setStatus("warning");
    });
    return () => {
      active = false;
    };
  }, [storyId, userId]);
  async function confirm() {
    if (status !== "warning") return;
    setError(null);
    if (!userId) {
      setStatus("accepted");
      try {
        sessionStorage.setItem(storageKey, "confirmed");
        window.dispatchEvent(new Event(confirmationEvent));
      } catch {
        // Browser storage may be disabled; retain consent for this visit.
      }
      return;
    }
    setStatus("saving");
    try {
      const result_0 = await confirmMatureContent(storyId);
      if (result_0.userId !== userId) throw new Error("Session changed");
      setStatus("accepted");
    } catch {
      setError("Could not save your confirmation. Please try again.");
      setStatus("warning");
    }
  }
  return {
    accepted: status === "accepted" || guestAccepted,
    checking: status === "checking",
    saving: status === "saving",
    error,
    confirm
  };
}